const MODULE_NAME = "_63_futures_trade_table";

import React, { useEffect, useState } from "react";
import { cachedGet } from "./apiCache.js";
import { useLocation } from "react-router-dom";
import axios from "axios";

const DATE_FILTERS = [
    { label: "All", value: "all" },
    { label: "Past 90 Days", value: "90days" },
    { label: "Year-to-Date", value: "ytd" }
];

const POSITION_STATUS_OPTIONS = ["All", "Open", "Closed", "Expired", "Partial Close"];

const CONTRACT_MULTIPLIERS = {
    "ES": 50,
    "NQ": 20,
    "YM": 5,
    "MES": 5,
    "MNQ": 2,
    "MYM": 0.5
};

function Tooltip({ text }) {
    return (
        <span
            style={{
                marginLeft: 4,
                verticalAlign: "middle",
                cursor: "pointer",
                color: "#888"
            }}
            title={text}
        >
            🛈
        </span>
    );
}

function FuturesTradeTable({ activeDate, latest_date, refreshKey }) {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    let dateFromQuery = queryParams.get("date");
    if (!dateFromQuery || !/^\d{4}-\d{2}-\d{2}$/.test(dateFromQuery)) {
        dateFromQuery = null;
    }
    const effectiveDate = dateFromQuery
        || (latest_date && /^\d{4}-\d{2}-\d{2}$/.test(latest_date) ? latest_date : null)
        || (sessionStorage.getItem("latest_date") && /^\d{4}-\d{2}-\d{2}$/.test(sessionStorage.getItem("latest_date")) ? sessionStorage.getItem("latest_date") : null)
        || new Date().toISOString().slice(0, 10);

    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [statusUpdating, setStatusUpdating] = useState({});
    const [dateFilter, setDateFilter] = useState("all");
    const [positionStatusFilter, setPositionStatusFilter] = useState("All");
    const [expandedGroups, setExpandedGroups] = useState({});

    const formatHeadlineDate = (dateStr) => {
        if (!dateStr) return "";
        const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            const [year, month, day] = [isoMatch[1], isoMatch[2], isoMatch[3]];
            const dateObj = new Date(`${year}-${month}-${day}T00:00:00`);
            if (!isNaN(dateObj)) {
                const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" });
                const monthAbbr = dateObj.toLocaleDateString("en-US", { month: "short" });
                const dayNum = dateObj.getDate();
                return `${weekday} ${monthAbbr} ${dayNum}`;
            }
        }
        const dateObj = new Date(dateStr);
        if (!isNaN(dateObj)) {
            const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" });
            const monthAbbr = dateObj.toLocaleDateString("en-US", { month: "short" });
            const dayNum = dateObj.getDate();
            return `${weekday} ${monthAbbr} ${dayNum}`;
        }
        return dateStr;
    };

    const filterRows = (rows) => {
        let filtered = [...rows];

        if (dateFilter !== "all") {
            const now = new Date();
            filtered = filtered.filter(row => {
                if (!row.Entry_Date && !row.Close_Date) return false;

                const dateStr = row.Entry_Date || row.Close_Date;
                const parts = dateStr.split("/");
                if (parts.length !== 3) return false;

                const [mm, dd, yy] = parts;
                const rowDate = new Date(`20${yy}`, parseInt(mm) - 1, parseInt(dd));

                if (dateFilter === "90days") {
                    const diff = (now - rowDate) / (1000 * 60 * 60 * 24);
                    return diff <= 90;
                }
                if (dateFilter === "ytd") {
                    return rowDate.getFullYear() === now.getFullYear();
                }
                return true;
            });
        }

        if (positionStatusFilter !== "All") {
            filtered = filtered.filter(row => {
                return row.position_status === positionStatusFilter;
            });
        }

        return filtered;
    };

    const groupTradesBySummary = (rows) => {
        const grouped = {};

        rows.forEach((row, idx) => {
            const groupKey = `${row.Root_Symbol}|${row.Entry_Date}|${row.Close_Date}`;

            if (!grouped[groupKey]) {
                grouped[groupKey] = {
                    index: Object.keys(grouped).length,
                    root_symbol: row.Root_Symbol,
                    display_symbol: row.Display_Symbol,
                    entry_date: row.Entry_Date,
                    close_date: row.Close_Date,
                    expiration_date: row.Expiration_Date,
                    position_status: row.position_status,
                    exchange: row.Exchange,
                    contract_code: row.Contract_Code,
                    trades: [],
                    total_pl: 0,
                    total_qty: 0,
                    num_trades: 0
                };
            }

            grouped[groupKey].trades.push(row);
            grouped[groupKey].total_pl += row.PL || 0;
            grouped[groupKey].total_qty += (row.Qty || 0);
            grouped[groupKey].num_trades += 1;
        });

        // Calculate average prices for each group
        Object.values(grouped).forEach(group => {
            let avgEntry = 0;
            let avgClose = 0;
            let totalQtyForEntry = 0;
            let totalQtyForClose = 0;

            group.trades.forEach(trade => {
                if (trade.Entry_Price && trade.Qty) {
                    avgEntry += trade.Entry_Price * trade.Qty;
                    totalQtyForEntry += trade.Qty;
                }
                if (trade.Close_Price && trade.Qty) {
                    avgClose += trade.Close_Price * trade.Qty;
                    totalQtyForClose += trade.Qty;
                }
            });

            group.avg_entry_price = totalQtyForEntry > 0 ? avgEntry / totalQtyForEntry : 0;
            group.avg_close_price = totalQtyForClose > 0 ? avgClose / totalQtyForClose : 0;
        });

        return Object.values(grouped);
    };

    const getBadgeColor = (direction) => {
        const d = (direction || "").toLowerCase();
        if (d === "long") return "#2196F3";
        if (d === "short") return "#F44336";
        return "#888";
    };

    const getDirection = (sideStr) => {
        if (!sideStr) return "Unknown";
        const side = (sideStr || "").toUpperCase();
        if (side === "BUY") return "Long";
        if (side === "SELL") return "Short";
        return "Unknown";
    };

    const handleExpandGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    useEffect(() => {
        setError("");
        setData(null);
        if (!effectiveDate) {
            setError("No date available for futures trade data.");
            return;
        }

        cachedGet(
            `${process.env.REACT_APP_API_URL}/api/futures-trade-table`,
            { date: effectiveDate, refreshKey },
            { withCredentials: true }
        )
            .then(res => {
                console.log("[Futures] API Response:", res.data);
                setData(res.data);
                setExpandedGroups({});
            })
            .catch((err) => {
                console.error("[Futures] API Error:", err);
                setError("Could not fetch futures trade data");
            });
    }, [effectiveDate, refreshKey, location.search]);

    if (error) return <div style={{ color: "#fff" }}>{error}</div>;
    if (!data) return <div style={{ color: "#fff" }}>Loading...</div>;
    if (data.error && data.error.toLowerCase().includes("no futures trades")) {
        return null;
    }
    if (data.error) return <div style={{ color: "#fff" }}>{data.error}</div>;

    const filteredData = filterRows(data.summary || []);
    const groupedData = groupTradesBySummary(filteredData);
    const totalPL = typeof data.total_futures_pl === "number" ? data.total_futures_pl : 0;

    async function handleStatusChange(group, newStatus) {
        const rowKey = `${group.root_symbol}|${group.contract_code}|${group.exchange}`;
        setStatusUpdating(prev => ({ ...prev, [rowKey]: true }));

        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/api/update-futures-position-status`, {
                root_symbol: group.root_symbol,
                contract_code: group.contract_code,
                exchange: group.exchange,
                position_status: newStatus
            }, { withCredentials: true });

            setData(prev => {
                if (!prev || !Array.isArray(prev.summary)) return prev;
                const updatedSummary = prev.summary.map(trade => {
                    if (trade.Root_Symbol === group.root_symbol &&
                        trade.Contract_Code === group.contract_code &&
                        trade.Exchange === group.exchange) {
                        return { ...trade, position_status: newStatus };
                    }
                    return trade;
                });
                return { ...prev, summary: updatedSummary };
            });
        } catch (err) {
            alert("Failed to update position status.");
        } finally {
            setStatusUpdating(prev => ({ ...prev, [rowKey]: false }));
        }
    }

    return (
        <div data-analytics={MODULE_NAME}>
            <h4
                className="collapse-toggle-headline"
                style={{ color: "#fff", display: "flex", alignItems: "center", marginBottom: "16px" }}
            >
                📊 Futures – {formatHeadlineDate(data.date)}
                <Tooltip text={data.tooltip || ""} />
            </h4>

            <div style={{ margin: "8px 0", display: "flex", gap: 16, color: "#fff", flexWrap: "wrap" }}>
                <label>
                    Date Filter:&nbsp;
                    <select
                        value={dateFilter}
                        onChange={e => setDateFilter(e.target.value)}
                        style={{ minWidth: 120, padding: "4px 8px", background: "#1a1a1a", color: "#fff", border: "1px solid #444", borderRadius: 4 }}
                    >
                        {DATE_FILTERS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </label>
                <label>
                    Position Status:&nbsp;
                    <select
                        value={positionStatusFilter}
                        onChange={e => setPositionStatusFilter(e.target.value)}
                        style={{ minWidth: 120, padding: "4px 8px", background: "#1a1a1a", color: "#fff", border: "1px solid #444", borderRadius: 4 }}
                    >
                        {POSITION_STATUS_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </label>
            </div>

            {groupedData.length > 0 ? (
                <>
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px", color: "#fff" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid #444", backgroundColor: "#1a1a1a" }}>
                                <th style={{ padding: "12px", textAlign: "left", fontWeight: 600, color: "#aaa" }}>Instrument</th>
                                <th style={{ padding: "12px", textAlign: "center", fontWeight: 600, color: "#aaa" }}>Entry Date</th>
                                <th style={{ padding: "12px", textAlign: "center", fontWeight: 600, color: "#aaa" }}>Close Date</th>
                                <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: "#aaa" }}>Avg Entry Price</th>
                                <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: "#aaa" }}>Avg Close Price</th>
                                <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: "#aaa" }}>Qty</th>
                                <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: "#aaa" }}>Total P/L</th>
                                <th style={{ padding: "12px", textAlign: "center", fontWeight: 600, color: "#aaa" }}>Trades</th>
                                <th style={{ padding: "12px", textAlign: "center", fontWeight: 600, color: "#aaa" }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedData.map((group, idx) => {
                                const groupKey = `${group.root_symbol}|${group.entry_date}|${group.close_date}`;
                                const isExpanded = expandedGroups[groupKey];
                                const pl = group.total_pl || 0;

                                return (
                                    <React.Fragment key={groupKey}>
                                        {/* Group Header Row */}
                                        <tr
                                            style={{
                                                borderBottom: "1px solid #333",
                                                backgroundColor: idx % 2 === 0 ? "#0f0f0f" : "#111",
                                                fontWeight: "bold"
                                            }}
                                        >
                                            <td style={{ padding: "12px", textAlign: "left", display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span
                                                    style={{
                                                        width: "20px",
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                        userSelect: "none"
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleExpandGroup(groupKey);
                                                    }}
                                                    title="Click to expand/collapse"
                                                >
                                                    {isExpanded ? "▼" : "▶"}
                                                </span>
                                                <div>
                                                    <div style={{ fontWeight: 600, color: "#fff" }}>{group.display_symbol}</div>
                                                    <div style={{ fontSize: "12px", color: "#aaa" }}>Exp: {group.expiration_date}</div>
                                                </div>
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "center", color: "#eee", fontSize: "12px" }}>
                                                {group.entry_date || "—"}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "center", color: "#eee", fontSize: "12px" }}>
                                                {group.close_date || "—"}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "right", color: "#eee" }}>
                                                ${group.avg_entry_price.toFixed(2)}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "right", color: "#eee" }}>
                                                {group.avg_close_price ? `$${group.avg_close_price.toFixed(2)}` : "—"}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "right", color: "#eee" }}>
                                                {group.total_qty}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>
                                                <span style={{
                                                    color: pl >= 0 ? "#4CAF50" : "#F44336"
                                                }}>
                                                    ${Math.abs(pl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "center", color: "#aaa" }}>
                                                {group.num_trades}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "center" }}>
                                                <select
                                                    value={group.position_status}
                                                    disabled={!!statusUpdating[`${group.root_symbol}|${group.contract_code}|${group.exchange}`]}
                                                    onChange={e => handleStatusChange(group, e.target.value)}
                                                    style={{
                                                        padding: "4px 8px",
                                                        borderRadius: 4,
                                                        border: "1px solid #444",
                                                        fontSize: "12px",
                                                        background: "#222",
                                                        color: "#fff",
                                                        cursor: statusUpdating[`${group.root_symbol}|${group.contract_code}|${group.exchange}`] ? "wait" : "pointer"
                                                    }}
                                                >
                                                    {POSITION_STATUS_OPTIONS.filter(o => o !== "All").map(opt => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>

                                        {/* Expanded Individual Trade Rows */}
                                        {isExpanded && group.trades.map((trade, tradeIdx) => {
                                            const tradePL = trade.PL || 0;
                                            const direction = getDirection(trade.Side);

                                            return (
                                                <tr
                                                    key={`trade_${groupKey}_${tradeIdx}`}
                                                    style={{
                                                        background: "#1a1d26",
                                                        fontSize: "12px",
                                                        borderLeft: "3px solid #1976d2"
                                                    }}
                                                >
                                                    <td style={{ padding: "8px 12px", paddingLeft: 50 }}>
                                                        <div style={{ color: "#aaa" }}>Individual Trade</div>
                                                    </td>
                                                    <td style={{ padding: "8px 12px", textAlign: "center", fontSize: "11px", color: "#888" }}>
                                                        {trade.Entry_Date || "—"}
                                                    </td>
                                                    <td style={{ padding: "8px 12px", textAlign: "center", fontSize: "11px", color: "#888" }}>
                                                        {trade.Close_Date || "—"}
                                                    </td>
                                                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#eee" }}>
                                                        ${Number(trade.Entry_Price || 0).toFixed(2)}
                                                    </td>
                                                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#eee" }}>
                                                        {trade.Close_Price ? `$${Number(trade.Close_Price).toFixed(2)}` : "—"}
                                                    </td>
                                                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#eee" }}>
                                                        {trade.Qty}
                                                    </td>
                                                    <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600 }}>
                                                        <span style={{
                                                            color: tradePL >= 0 ? "#4CAF50" : "#F44336"
                                                        }}>
                                                            ${Math.abs(tradePL).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: "8px 12px", textAlign: "center", color: "#999" }}>
                                                        —
                                                    </td>
                                                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                                        <span style={{
                                                            background: getBadgeColor(direction),
                                                            color: "#fff",
                                                            padding: "2px 6px",
                                                            borderRadius: 3,
                                                            fontSize: "10px",
                                                            fontWeight: "bold",
                                                            textTransform: "uppercase",
                                                            display: "inline-block"
                                                        }}>
                                                            {direction}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>

                    <div style={{ marginTop: "24px", textAlign: "center", fontWeight: "bold", fontSize: "1.2em", color: "#fff" }}>
                        Total Profit/Loss (Closed Futures): <span style={{ color: totalPL >= 0 ? "#4CAF50" : "#F44336" }}>
                            ${totalPL.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                    </div>
                </>
            ) : (
                <div style={{ color: "#aaa", padding: "16px", textAlign: "center" }}>
                    No futures trades found for selected filters.
                </div>
            )}
        </div>
    );
}

export default FuturesTradeTable;